import { z } from "zod";

export const situationSlotKeys = [
  "identity",
  "trigger",
  "scope",
  "signals",
  "underlying-causes",
  "actors",
  "stake",
  "governing-instrument",
  "norms",
  "preconditions",
  "goal",
  "decision-rule",
  "steps",
  "skills-invoked",
  "supporting-resources",
  "risks",
  "outcome/escalation",
  "evaluation",
  "rationale",
  "stance",
  "timeline",
] as const;

export const situationSlotKeySchema = z.enum(situationSlotKeys);
export const situationRoleSchema = z.enum(["PO", "VO", "MBO"]);
export const situationSourceKindSchema = z.enum(["url", "pdf", "cursus", "podcast"]);
export const situationCardStatusSchema = z.enum(["published", "concept", "review", "processing"]);
export const situationVersionStatusSchema = z.enum(["concept", "published", "superseded", "review", "processing"]);
export const situationSlotChangeSchema = z.enum(["added", "changed", "removed", "unchanged"]);

export const situationActorSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
});

export const situationSourceSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  kind: situationSourceKindSchema,
  tag: z.string().min(1),
  href: z.string().min(1),
  excerpt: z.string().min(1),
});

export const situationCardSourceSchema = situationSourceSchema.extend({
  isNew: z.boolean(),
});

export const situationCardListItemSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  summary: z.string().min(1),
  roles: z.array(situationRoleSchema).min(1),
  sourceCount: z.number().int().nonnegative(),
  filledSlotCount: z.number().int().nonnegative(),
  groundedness: z.number().int().min(0).max(100),
  status: situationCardStatusSchema,
  publishedVersion: z.number().int().positive(),
  conceptVersion: z.number().int().positive().nullable(),
  changedAt: z.string().datetime(),
  changedBy: situationActorSchema,
});

export const situationCardListSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.literal(20),
  total: z.number().int().nonnegative(),
  libraryCount: z.number().int().nonnegative(),
  conceptCount: z.number().int().nonnegative(),
  items: z.array(situationCardListItemSchema),
});

export const situationCardSlotSchema = z.object({
  key: situationSlotKeySchema,
  text: z.string().nullable(),
  publishedText: z.string().nullable(),
  sourceIds: z.array(z.string().min(1)),
  change: situationSlotChangeSchema,
});

export const situationCardDetailSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  summary: z.string().min(1),
  roles: z.array(situationRoleSchema).min(1),
  status: situationCardStatusSchema,
  publishedVersion: z.number().int().positive(),
  conceptVersion: z.number().int().positive().nullable(),
  changedAt: z.string().datetime(),
  changedBy: situationActorSchema,
  reviewer: situationActorSchema.nullable(),
  slots: z.array(situationCardSlotSchema).length(21),
  cardSources: z.array(situationCardSourceSchema),
  removedSources: z.array(situationSourceSchema),
  canGenerate: z.boolean(),
  canPublish: z.boolean(),
  groundedness: z.number().int().min(0).max(100),
});

export const situationSourceSelectionSchema = z.object({
  sourceIds: z.array(z.string().min(1)),
});

export const situationRoleSelectionSchema = z.object({
  roles: z.array(situationRoleSchema).min(1),
});

export const situationVersionSummarySchema = z.object({
  version: z.number().int().positive(),
  status: situationVersionStatusSchema,
  changedAt: z.string().datetime(),
  changedBy: situationActorSchema,
  publishedAt: z.string().datetime().nullable(),
  publishedBy: situationActorSchema.nullable(),
  canRestore: z.boolean(),
});

export const situationVersionListSchema = z.object({
  cardId: z.string().min(1),
  title: z.string().min(1),
  versions: z.array(situationVersionSummarySchema),
});

export const situationVersionSlotSchema = z.object({
  key: situationSlotKeySchema,
  text: z.string().nullable(),
  sourceIds: z.array(z.string().min(1)),
});

export const situationVersionDetailSchema = situationVersionSummarySchema.extend({
  cardId: z.string().min(1),
  title: z.string().min(1),
  summary: z.string().min(1),
  cardSources: z.array(situationSourceSchema),
  slots: z.array(situationVersionSlotSchema).length(21),
});

export const situationSourcePageSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.literal(10),
  total: z.number().int().nonnegative(),
  items: z.array(situationSourceSchema),
});

export const situationPopularSourcesSchema = z.object({
  items: z.array(situationSourceSchema.extend({ usageCount: z.number().int().nonnegative() })).max(5),
});

export const situationPublishAllResultSchema = z.object({
  published: z.array(
    z.object({
      cardId: z.string().min(1),
      version: z.number().int().positive(),
    }),
  ),
});

export type SituationSource = z.infer<typeof situationSourceSchema>;
export type SituationCardSource = z.infer<typeof situationCardSourceSchema>;
export type SituationSlotKey = z.infer<typeof situationSlotKeySchema>;
export type SituationRole = z.infer<typeof situationRoleSchema>;
export type SituationSourceKind = z.infer<typeof situationSourceKindSchema>;
export type SituationCardList = z.infer<typeof situationCardListSchema>;
export type SituationCardDetail = z.infer<typeof situationCardDetailSchema>;
export type SituationVersionList = z.infer<typeof situationVersionListSchema>;
export type SituationVersionDetail = z.infer<typeof situationVersionDetailSchema>;
export type SituationSourcePage = z.infer<typeof situationSourcePageSchema>;
export type SituationPopularSources = z.infer<typeof situationPopularSourcesSchema>;
export type SituationPublishAllResult = z.infer<typeof situationPublishAllResultSchema>;
